import React, { useEffect } from 'react';
import { Box, LoadingOverlay } from '@mantine/core';
import { useQRCode } from '@/hooks/useQRCode';
import { QRStyleConfig, QRFrameType } from '@/utils/qrcode/types';
import { QRCodeFrame } from './QRCodeFrame';

interface QRCodeProps extends QRStyleConfig {
    className?: string;
    style?: React.CSSProperties;
    loading?: boolean;
    width?: number;
    height?: number;
    frameType?: QRFrameType;
    frameColor?: string;
    frameText?: string;
    frameTextColor?: string;
}

export function QRCode({ 
    className, 
    style, 
    loading = false,
    width = 300,
    height = 300,
    frameType = 'none',
    frameColor = '#000000',
    frameText = 'SCAN ME',
    frameTextColor = '#FFFFFF',
    ...config 
}: QRCodeProps) {
    const { containerRef, updateQR } = useQRCode({
        ...config,
        width: frameType === 'none' ? width : Math.max(width - 40, 100), // Adjust width for frames
        height: frameType === 'none' ? height : Math.max(height - 40, 100) // Adjust height for frames
    });

    useEffect(() => {
        updateQR({
            ...config,
            width: frameType === 'none' ? width : Math.max(width - 40, 100), // Adjust width for frames
            height: frameType === 'none' ? height : Math.max(height - 40, 100) // Adjust height for frames
        });
    }, [
        // Include all the properties that should trigger an update
        config.value,
        config.dotsOptions?.color,
        config.dotsOptions?.type,
        config.dotsOptions?.backgroundColor,
        config.cornersSquareOptions?.color,
        config.cornersSquareOptions?.type,
        config.cornersSquareOptions?.backgroundColor,
        config.cornersDotOptions?.color,
        config.cornersDotOptions?.type,
        config.cornersDotOptions?.backgroundColor,
        config.backgroundOptions?.color,
        config.image,
        config.imageOptions?.imageSize,
        config.imageOptions?.margin,
        config.imageOptions?.hideBackgroundDots,
        width,
        height,
        frameType, // Add frameType to dependencies
        frameColor, // Add frameColor to dependencies
        frameText, // Add frameText to dependencies
        frameTextColor, // Add frameTextColor to dependencies
        updateQR
    ]);

    return (
        <Box 
            className={className} 
            style={{ 
                position: 'relative',
                width: width,
                height: frameType === 'none' ? height : 'auto', // Allow height to adjust with frame
                padding: 0,
                margin: 0,
                ...style
            }}
        >
            <QRCodeFrame
                frameType={frameType}
                frameColor={frameColor}
                frameText={frameText}
                frameTextColor={frameTextColor}
            >
                <Box style={{ 
                    width: '100%', 
                    height: '100%', 
                    position: 'relative',
                    padding: 0,
                    margin: 0,
                    overflow: 'hidden'
                }}>
                    {loading && <LoadingOverlay visible={true} />}
                    <div 
                        ref={containerRef} 
                        style={{ 
                            width: '100%', 
                            height: '100%',
                            padding: 0,
                            margin: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }} 
                    />
                </Box>
            </QRCodeFrame>
        </Box>
    );
}